// Fermata HOLDS for playback: the band lands together and time stretches.
//
// A fermata bar plays as one sustained hit — the melody note, a single chord
// strum and the bass root strike its downbeat and ring while the scheduler
// stretches the bar's beats by HOLD_STRETCH (metronome and drums stay silent
// for the window; the player owns that half — see ArrangementOptions.holds).
// This module is the pure data half: derive the hold windows from the
// playback form's fermata bars and thin/extend the note tracks so nothing
// busy plays through the hold.

import type { IRealMeasure } from '@/music/ireal/types';
import { measureStartBeats } from '@/music/chart';
import type { CompHit } from '@/music/comping';

/** How much a held bar stretches: its beats last this many times longer. */
export const HOLD_STRETCH = 2;

/** A held span in absolute in-form beats (a fermata bar, start inclusive). */
export interface HoldWindow {
  start: number;
  end: number;
}

/** Hold windows for every fermata bar of a flattened playback form. */
export function fermataHoldWindows(
  measures: Pick<IRealMeasure, 'timeSig' | 'pickupBeats' | 'fermata'>[],
  defaultTimeSig: [number, number],
): HoldWindow[] | undefined {
  const starts = measureStartBeats(measures, defaultTimeSig);
  const out: HoldWindow[] = [];
  let meter = defaultTimeSig;
  measures.forEach((m, i) => {
    meter = m.timeSig ?? meter;
    if (!m.fermata) return;
    out.push({ start: starts[i], end: starts[i] + (m.pickupBeats ?? meter[0]) });
  });
  return out.length > 0 ? out : undefined;
}

const inWindow = (beat: number, w: HoldWindow) => beat >= w.start - 1e-3 && beat < w.end - 1e-3;

/**
 * Replace a held bar's comp figure with ONE sustained hit per chord (Infinity
 * ring — the player never damps it; the next chord's strike takes over): the
 * chord sounding at the hold's start, and any chord the held bar changes to.
 * Stabs and offbeat figures through a fermata read as the band ignoring the
 * hold, and a held Dm7 G7 bar must still end on the G7.
 */
export function sustainCompHitsForHolds(
  hits: CompHit[] | undefined,
  holds: HoldWindow[] | undefined,
  events: { startBeat: number; durationBeats: number }[],
): CompHit[] | undefined {
  if (!hits || !holds || holds.length === 0) return hits;
  const out = hits.filter((h) => !holds.some((w) => inWindow(h.beat, w)));
  for (const w of holds) {
    // Every chord the window touches rings; an N.C. hold strikes nothing.
    events.forEach((e, idx) => {
      const sounding =
        e.startBeat <= w.start + 1e-3 && e.startBeat + e.durationBeats > w.start + 1e-3;
      if (!sounding && !inWindow(e.startBeat, w)) return;
      out.push({
        chordIndex: idx,
        beat: Math.max(e.startBeat, w.start),
        durationBeats: Infinity,
        velocity: 0.9,
      });
    });
  }
  return out.sort((a, b) => a.beat - b.beat);
}

/**
 * A held bar's bass is one root per chord, held: keep the note that states
 * each chord (the downbeat root by the walking-bass invariant, or the root an
 * anticipation pushed half a beat early), stretched to the held bar's real
 * length up to the next kept note, and drop the walking that followed it.
 * Without `chords` the whole window is one chord.
 */
export function thinBassForHolds<N extends { beat: number; durationBeats: number }>(
  line: N[],
  holds: HoldWindow[] | undefined,
  chords?: { startBeat: number; durationBeats: number }[],
): N[] {
  if (!holds || holds.length === 0) return line;
  const until = new Map<N, number>();
  for (const w of holds) {
    // Each chord's share of the window starts where the chord does, or at the
    // window's start for the chord already sounding there (or for no chord).
    const starts = (chords ?? [])
      .filter((c) => c.startBeat < w.end - 1e-3 && c.startBeat + c.durationBeats > w.start + 1e-3)
      .map((c) => Math.max(c.startBeat, w.start))
      .sort((a, b) => a - b);
    if (starts.length === 0 || starts[0] > w.start + 1e-3) starts.unshift(w.start);
    const kept: N[] = [];
    starts.forEach((start, i) => {
      const end = starts[i + 1] ?? w.end;
      const note =
        line.find((n) => Math.abs(n.beat - start) < 1e-3) ??
        line.find(
          (n) =>
            !kept.includes(n) &&
            n.beat >= Math.max(w.start, start - 0.5) - 1e-3 &&
            n.beat < start - 1e-3,
        ) ??
        line.find((n) => n.beat >= start - 1e-3 && n.beat < end - 1e-3);
      if (note && !kept.includes(note)) kept.push(note);
    });
    kept.sort((a, b) => a.beat - b.beat);
    kept.forEach((n, i) => until.set(n, kept[i + 1]?.beat ?? w.end));
  }
  return line
    .filter((n) => !holds.some((w) => inWindow(n.beat, w)) || until.has(n))
    .map((n) => {
      const end = until.get(n);
      return end === undefined ? n : { ...n, durationBeats: (end - n.beat) * HOLD_STRETCH };
    });
}

/**
 * Extend notes sounding through a hold so they ring for the stretched time —
 * the scheduler dwells, but a note's seconds come from its durationBeats.
 * Used for the melody track (the held note itself).
 */
export function extendNotesForHolds<N extends { beat: number; durationBeats: number }>(
  notes: N[],
  holds: HoldWindow[] | undefined,
): N[] {
  if (!holds || holds.length === 0) return notes;
  return notes.map((n) => {
    let extra = 0;
    for (const w of holds) {
      const overlap = Math.min(n.beat + n.durationBeats, w.end) - Math.max(n.beat, w.start);
      if (overlap > 1e-3) extra += overlap * (HOLD_STRETCH - 1);
    }
    return extra > 0 ? { ...n, durationBeats: n.durationBeats + extra } : n;
  });
}
